import React, { useState, useEffect, useRef } from 'react';
import { AJLogoLoader } from './AJLogoLoader';
import { ModelSelectorModal } from './ModelSelectorModal';

interface LovablePreviewProps {
  initialHtml?: string;
  initialPrompt?: string;
  onReset: () => void;
}

interface TaskCompletion {
  model: 'gemini' | 'claude';
  task: string;
  timestamp: Date;
}

export const LovablePreview: React.FC<LovablePreviewProps> = ({ initialHtml, initialPrompt, onReset }) => {
  const [html, setHtml] = useState<string>(initialHtml || '');
  const [selectedModel, setSelectedModel] = useState<'gemini' | 'claude'>('gemini');
  const [editPrompt, setEditPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [taskHistory, setTaskHistory] = useState<TaskCompletion[]>(
    initialPrompt ? [{ model: 'gemini', task: initialPrompt, timestamp: new Date() }] : []
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (initialHtml) {
      setHtml(initialHtml);
    }
  }, [initialHtml]);

  useEffect(() => {
    if (html && iframeRef.current && iframeRef.current.contentWindow) {
      const doc = iframeRef.current.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
    }
  }, [html]);

  const handleGenerateOrEdit = async () => {
    if (!editPrompt.trim()) return;
    
    setIsGenerating(true);
    setEditError(null);
    
    try {
      const response = await fetch('/api/generateHtml', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: editPrompt,
          existingHtml: html || undefined,
          preferredModel: selectedModel
        })
      });

      // Get response text first, then try to parse as JSON
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', responseText.substring(0, 200));
        throw new Error('Server returned invalid response. Please try again or switch AI models.');
      }
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate website');
      }
      
      if (!data.html) {
        throw new Error('No HTML received from server');
      }
      
      setHtml(data.html);
      setTaskHistory(prev => [...prev, {
        model: selectedModel,
        task: editPrompt,
        timestamp: new Date()
      }]);
      setEditPrompt('');
      
    } catch (error) {
      console.error('Generation failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate website. Please try again.';
      setEditError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'website.html';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(html);
    alert('HTML code copied to clipboard!');
  };

  // Auto-resize textarea
  useEffect(() => {
    if (editInputRef.current) {
      editInputRef.current.style.height = 'auto';
      editInputRef.current.style.height = Math.min(editInputRef.current.scrollHeight, 120) + 'px';
    }
  }, [editPrompt]);

  return (
    <div className="flex flex-col lg:flex-row h-screen overflow-hidden">
      {/* Left Sidebar - Controls (Desktop Only) */}
      <div className="hidden lg:flex lg:flex-col lg:w-[420px] xl:w-[480px] bg-white dark:bg-brand-black border-r border-gray-200 dark:border-white/10 flex-shrink-0">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Website Builder</h2>
            <button 
              onClick={onReset}
              className="text-sm px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors"
            >
              <svg className="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Project
            </button>
          </div>
          
          {/* Model Selector */}
          <div className="flex items-center gap-2 p-3 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800">
            <img 
              src={selectedModel === 'gemini' ? '/providers/logos/google.svg' : '/providers/logos/anthropic.svg'} 
              alt={selectedModel === 'gemini' ? 'Gemini' : 'Claude'}
              className="w-8 h-8 rounded-lg border-2 border-white dark:border-gray-800 shadow-sm"
            />
            <div className="flex-1">
              <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">AI Model</div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">
                {selectedModel === 'gemini' ? '✨ Gemini 2.5 Pro' : '🧠 Claude Opus 4'}
              </div>
            </div>
            <button
              onClick={() => setIsModelSelectorOpen(true)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/50 border border-purple-200 dark:border-purple-800 transition-colors"
            >
              Change
            </button>
          </div>
        </div>

        {/* Edit Input Section */}
        <div className="p-6 flex-1 overflow-y-auto">
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
            {html ? 'Make Changes' : 'Describe Your Website'}
          </h3>
          
          {/* Edit Textarea */}
          <div className="relative">
            <textarea
              ref={editInputRef}
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !isGenerating) {
                  e.preventDefault();
                  handleGenerateOrEdit();
                }
              }}
              placeholder={html ? "Describe changes... (e.g., 'Change hero background to blue gradient', 'Add testimonials section')" : "Describe your website... (e.g., 'A modern restaurant website', 'Portfolio for a photographer')"}
              disabled={isGenerating}
              className="w-full min-h-[120px] max-h-[200px] p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
            
            {/* Error Message */}
            {editError && (
              <div className="mt-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                <p className="text-xs text-red-700 dark:text-red-400 flex items-start gap-2">
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {editError}
                </p>
              </div>
            )}
            
            {/* Submit Button */}
            <button
              onClick={handleGenerateOrEdit}
              disabled={isGenerating || !editPrompt.trim()}
              className="w-full mt-3 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-300 disabled:to-gray-400 dark:disabled:from-gray-700 dark:disabled:to-gray-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <>
                  <AJLogoLoader size="sm" />
                  <span>{html ? 'Applying Changes...' : 'Generating Website...'}</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>{html ? 'Apply Changes' : 'Generate Website'}</span>
                </>
              )}
            </button>
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
              Press Enter to submit or Shift+Enter for new line
            </p>
          </div>

          {/* Task History */}
          {taskHistory.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Generation History</h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {taskHistory.slice().reverse().map((task, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${
                        task.model === 'gemini' 
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                          : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                      }`}>
                        <img 
                          src={task.model === 'gemini' ? '/providers/logos/google.svg' : '/providers/logos/anthropic.svg'}
                          alt={task.model}
                          className="w-3 h-3"
                        />
                        {task.model === 'gemini' ? 'Gemini' : 'Claude'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {task.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2">{task.task}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {html && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-800 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleCopyCode}
                className="py-2.5 px-4 rounded-xl bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                Copy HTML
              </button>
              <button
                onClick={handleDownload}
                className="py-2.5 px-4 rounded-xl bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Top Toolbar (Mobile Only) */}
      <div className="lg:hidden bg-white dark:bg-gray-800 p-3 flex justify-between items-center border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <button onClick={onReset} className="btn-secondary-mobile">New</button>
        <div className="flex gap-2">
          {html && (
            <>
              <button onClick={handleCopyCode} className="btn-secondary-mobile">Copy</button>
              <button onClick={handleDownload} className="btn-primary-mobile">Download</button>
            </>
          )}
        </div>
      </div>

      {/* Preview Area - Right Side */}
      <div className="flex-1 bg-white dark:bg-brand-black overflow-hidden relative flex flex-col">
        {/* Desktop Preview Header */}
        <div className="hidden lg:block bg-gradient-to-r from-gray-50 to-gray-100 dark:from-black dark:to-brand-black p-3 border-b border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                Live Preview
              </div>
            </div>
          </div>
        </div>

        {/* iframe Container */}
        <div className="flex-1 overflow-hidden">
          {html ? (
            <iframe
              ref={iframeRef}
              title="Website Preview"
              className="w-full h-full border-0"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md px-6">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl flex items-center justify-center shadow-2xl mb-6 mx-auto">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  Start Building Your Website
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Describe your website in the sidebar and watch it come to life instantly with AI
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 rounded-xl border border-purple-200 dark:border-purple-800">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Powered by</span>
                  <img src="/providers/logos/google.svg" alt="Gemini" className="w-5 h-5" />
                  <span className="text-sm font-bold text-purple-600 dark:text-purple-400">Gemini 2.5 Pro</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Edit Input Bar (Mobile Only) */}
      <div className="lg:hidden flex-shrink-0 bg-gradient-to-t from-white via-white to-white/80 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900/80 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative flex items-end gap-2 bg-white dark:bg-gray-800 rounded-2xl p-2 shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(0,0,0,0.3)] border-2 border-gray-200/50 dark:border-gray-700/50 hover:border-brand-coral/30 dark:hover:border-brand-coral/30 transition-all duration-200">
            <div className="flex-1 flex items-end">
              <textarea
                ref={editInputRef}
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isGenerating) {
                    e.preventDefault();
                    handleGenerateOrEdit();
                  }
                }}
                placeholder={html ? "Make changes..." : "Describe your website..."}
                rows={1}
                disabled={isGenerating}
                className="flex min-h-[52px] w-full rounded-xl border-0 bg-transparent px-4 py-3.5 text-base text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none max-h-[120px] overflow-y-auto"
              />
            </div>
            <button
              onClick={handleGenerateOrEdit}
              disabled={isGenerating || !editPrompt.trim()}
              className="flex-shrink-0 h-11 w-11 p-0 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-300 disabled:to-gray-400 dark:disabled:from-gray-700 dark:disabled:to-gray-600 disabled:cursor-not-allowed text-white shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105 disabled:hover:scale-100 disabled:hover:shadow-lg flex items-center justify-center mb-0.5 overflow-hidden"
            >
              {isGenerating ? (
                <div className="scale-50">
                  <AJLogoLoader size="sm" />
                </div>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              )}
              <span className="sr-only">{isGenerating ? 'Generating...' : 'Generate'}</span>
            </button>
          </div>
          <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
            Powered by {selectedModel === 'claude' ? '🧠 Claude' : '✨ Gemini'}
          </p>
        </div>
      </div>

      {/* Modals */}
      {isModelSelectorOpen && (
        <ModelSelectorModal
          isOpen={isModelSelectorOpen}
          onClose={() => setIsModelSelectorOpen(false)}
          selectedModel={selectedModel}
          onSelectModel={(model) => {
            setSelectedModel(model);
            setIsModelSelectorOpen(false);
          }}
        />
      )}

      <style>{`
        .btn-primary-mobile { background-color: #36454F; color: white; padding: 6px 12px; border-radius: 8px; font-weight: 600; font-size: 0.875rem; }
        .btn-secondary-mobile { background-color: #F0F4F8; color: #36454F; padding: 6px 12px; border-radius: 8px; font-weight: 600; font-size: 0.875rem; }
      `}</style>
    </div>
  );
};
